import { useState, useRef } from 'react';
import { MapPin, Phone, Mail, Clock, Upload, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    equipment_brand: '',
    equipment_model: '',
    service_type: 'reparation',
    description: ''
  });

  const serviceTypes = [
    { value: 'reparation', label: 'Réparation mécanique' },
    { value: 'peinture', label: 'Peinture' },
    { value: 'entretien', label: 'Entretien préventif' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 10 Mo)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      if (selectedFile) {
        formDataToSend.append('photo', selectedFile);
      }

      await axios.post(`${API}/quotes`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Votre demande de devis a été envoyée avec succès !');
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        equipment_brand: '',
        equipment_model: '',
        service_type: 'reparation',
        description: ''
      });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />
      <SEO
        title="Devis Gratuit Engins de Chantier La Réunion - Contact IDMAT Service"
        description="Demandez un devis gratuit pour la réparation, peinture ou entretien de vos engins de chantier à La Réunion. IDMAT Service — 27 avenue des artisans, 97436 Saint-Leu. Tél : 06 93 52 64 49. Réponse rapide garantie."
        canonical="/contact"
      />
      
      {/* Hero Section */}
      <section className="hero-section hero-contact min-h-[50vh]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Contact
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Demandez Votre Devis Gratuit
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Remplissez le formulaire ci-dessous et nous vous recontacterons dans les plus brefs délais.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 
                className="text-2xl font-bold text-[#1a1a1a] mb-8"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Informations de Contact
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <MapPin size={24} color="#1a1a1a" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1a1a1a] mb-1">Adresse</h4>
                    <p className="text-gray-600">
                      27 avenue des artisans<br />
                      97436 Saint-Leu<br />
                      La Réunion
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <Phone size={24} color="#1a1a1a" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1a1a1a] mb-1">Téléphone</h4>
                    <a 
                      href="tel:0693526449" 
                      className="text-gray-600 hover:text-[#F5C500] transition-colors"
                      data-testid="contact-phone"
                    >
                      06 93 52 64 49
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <Mail size={24} color="#1a1a1a" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1a1a1a] mb-1">Email</h4>
                    <a 
                      href="mailto:contact@idmatoi.com" 
                      className="text-gray-600 hover:text-[#F5C500] transition-colors"
                      data-testid="contact-email"
                    >
                      contact@idmatoi.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <Clock size={24} color="#1a1a1a" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1a1a1a] mb-1">Horaires</h4>
                    <p className="text-gray-600">
                      Lundi - Vendredi<br />
                      07h00 - 16h00
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-[#F4F4F5] border-l-4 border-[#F5C500]">
                <h4 className="font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Zone d'Intervention
                </h4>
                <p className="text-gray-600">
                  Nous intervenons sur toute l'île de La Réunion. 
                  Déplacement sur site possible.
                </p>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F4F4F5] p-8 md:p-12">
                <h2 
                  className="text-2xl font-bold text-[#1a1a1a] mb-8"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Formulaire de Demande de Devis
                </h2>

                <form onSubmit={handleSubmit} data-testid="quote-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className="input-industrial"
                        required
                        data-testid="input-firstname"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                        Nom *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className="input-industrial"
                        required
                        data-testid="input-lastname"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="input-industrial"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="input-industrial"
                        required
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                        Marque de l'engin *
                      </label>
                      <input
                        type="text"
                        name="equipment_brand"
                        value={formData.equipment_brand}
                        onChange={handleInputChange}
                        placeholder="Ex: Komatsu, Caterpillar..."
                        className="input-industrial"
                        required
                        data-testid="input-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                        Modèle *
                      </label>
                      <input
                        type="text"
                        name="equipment_model"
                        value={formData.equipment_model}
                        onChange={handleInputChange}
                        placeholder="Ex: PC200, 320D..."
                        className="input-industrial"
                        required
                        data-testid="input-model"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                      Type de service *
                    </label>
                    <select
                      name="service_type"
                      value={formData.service_type}
                      onChange={handleInputChange}
                      className="input-industrial"
                      required
                      data-testid="select-service"
                    >
                      {serviceTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                      Description du problème *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={5}
                      className="input-industrial"
                      placeholder="Décrivez le problème rencontré ou le service souhaité..."
                      required
                      data-testid="textarea-description"
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-2 uppercase tracking-wide">
                      Photo de l'engin (optionnel)
                    </label>
                    <div 
                      className="file-upload-zone"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="file-upload-zone"
                    >
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-4">
                          <CheckCircle size={24} className="text-green-500" />
                          <span className="text-gray-700">{selectedFile.name}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeFile(); }}
                            className="p-1 hover:bg-gray-200 rounded"
                            data-testid="remove-file"
                          >
                            <X size={20} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload size={32} className="text-gray-400" />
                          <span className="text-gray-600">Cliquez pour ajouter une photo</span>
                          <span className="text-gray-400 text-sm">JPG, PNG - Max 10 Mo</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-quote"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-gray py-20" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-bold text-[#1a1a1a] mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Notre Localisation
            </h2>
            <p className="text-gray-600">Zone d'intervention : Toute l'île de La Réunion</p>
          </div>
          
          <div className="map-container shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3752.5742267548654!2d55.2739!3d-21.1699!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDEwJzExLjYiUyA1NcKwMTYnMjYuMCJF!5e0!3m2!1sfr!2sfr!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation IDMAT Service"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
